import { Language, Pointer, Service } from '@yleisradio/areena-types';
import {
  favoritesRoutes,
  guideRoutes,
  historyRoutes,
  liveRoutes,
  packageRoutes,
  personalRoutes,
  serviceRoutes,
} from './routes';
import logger from 'services/logger';
import { getCanonicalBaseURL } from './url';

function getServicePathSegment(service: Service, language: Language): string {
  return serviceRoutes[service][language];
}

function getOmatPageClientPath(
  omatPageName: OmatPageName,
  service: Service,
  language: Language
): string | null {
  const servicePathSegment = getServicePathSegment(service, language);

  switch (omatPageName) {
    case 'favorites':
      return `/${servicePathSegment}/${favoritesRoutes[language]}`;
    case 'history':
      return `/${servicePathSegment}/${historyRoutes[language]}`;
    case 'omat':
    default:
      return `/${servicePathSegment}/${personalRoutes[language]}`;
  }
}

export function pointerToCanonicalURL(
  pointer: Pointer,
  language: Language,
  service: Service | undefined
): string | null {
  const path = pointerToClientURL(pointer, language, service);
  return path ? getCanonicalBaseURL(language) + path : null;
}

type OmatPageName = 'favorites' | 'history' | 'omat';

export function pointerToClientURL(
  pointer: Pointer,
  language: Language,
  service: Service | undefined
): string | null {
  switch (pointer.type) {
    case 'view': {
      const packageViewMatch = pointer.uri.match(
        /\/packages\/(30-\d+|57-[\w-]+)\.json/
      );

      if (packageViewMatch && service) {
        const packageId = packageViewMatch[1] as string;

        return `/${serviceRoutes[service][language]}/${packageRoutes[language]}/${packageId}`;
      }

      const omatPageMatch =
        /\/(tv|radio)-(favorites|history|omat)\.json/.exec(pointer.uri) || [];
      const serviceInPointer = omatPageMatch[1] as Service | undefined;
      const omatPageName = omatPageMatch[2] as OmatPageName | undefined;

      if (pointer.uri.includes('/live.json')) {
        return `/${liveRoutes[language]}`;
      }

      const guideMatch = /\/(tv|radio)-guides\/(\d{4}-\d{2}-\d{2})\.json/.exec(
        pointer.uri
      );

      if (guideMatch) {
        const service = guideMatch[1] as Service;
        const guideDate = guideMatch[2] as string;

        return `/${serviceRoutes[service][language]}/${guideRoutes[language]}?t=${guideDate}`;
      }

      if (serviceInPointer && omatPageName) {
        return getOmatPageClientPath(omatPageName, serviceInPointer, language);
      }

      logger.warn(`Unknown view pointer URL. pointer.uri: ${pointer.uri}`);
      break;
    }

    case 'player': {
      break;
    }

    default: {
      if (pointer.uri.startsWith('yleareena://')) {
        return yleareenaURIToClientURL(pointer.uri, language, service);
      }

      return pointer.uri;
    }
  }

  return null;
}

function yleareenaURIToClientURL(
  uri: string,
  language: Language,
  service: Service | undefined = 'tv'
): string | null {
  const appMatch = uri.match(/^yleareena:\/\/apps\/(tv|radio)$/);

  if (appMatch) {
    const targetService = appMatch[1] as Service;

    return `/${serviceRoutes[targetService][language]}`;
  }

  const itemMatch = uri.match(/^yleareena:\/\/items\/(1-\d+)$/);

  if (itemMatch) {
    const itemId = itemMatch[1];

    return `/${itemId}`;
  }

  const packageMatch = uri.match(/^yleareena:\/\/packages\/([\w-]+)$/);

  if (packageMatch && service) {
    const prefix = `/${serviceRoutes[service][language]}/${packageRoutes[language]}`;
    const packageId = packageMatch[1] as string;

    return `${prefix}/${packageId}`;
  }

  if (uri.startsWith('yleareena://services/')) return null;

  logger.error('Invalid yleareena URI');

  return null;
}

export const idFromPointer = (pointer: Pointer | undefined): string | null => {
  if (!pointer) return null;

  const matches = pointer.uri.match(
    /^(?:yle|lasten)areena:\/\/(?:items|services|packages)\/([\w-]+)(?:\?.*)?$/
  );

  return matches?.[1] || null;
};

/** @returns Service (tv or radio) or null if pointer doesn't include information about service */
export function serviceFromPointer(pointer: Pointer): Service | null {
  if (pointer.type === 'app') {
    if (pointer.uri === 'yleareena://apps/tv') return 'tv';
    if (pointer.uri === 'yleareena://apps/radio') return 'radio';
  }

  if (pointer.type === 'view') {
    if (pointer.uri.includes('/radio-')) return 'radio';
    if (pointer.uri.includes('/tv-')) return 'tv';
  }

  if (pointer.type === undefined) {
    if (
      Object.values(serviceRoutes.radio).some((serviceSegment) =>
        pointer.uri.startsWith(`/${serviceSegment}`)
      )
    )
      return 'radio';

    if (
      Object.values(serviceRoutes.tv).some((serviceSegment) =>
        pointer.uri.startsWith(`/${serviceSegment}`)
      )
    )
      return 'tv';
  }

  return null;
}

export function isPlayerPointer(pointer: Pointer | undefined): boolean {
  return !!pointer && pointer.type === 'player';
}
